import { useEffect, useMemo, useState } from "react";
import { NavButtons } from "../components/NavButtons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getCached, setCached } from "@/lib/cache";
import { toast } from "sonner";
import AdminThreadReplyBox from "@/components/AdminThreadReplyBox";
import { LinkifiedText } from "@/components/LinkifiedText";
import FilterPills from "@/components/FilterPills";
import {
  ArrowLeft, ArrowsClockwise, PaperPlaneTilt, CheckCircle, WarningCircle,
  CaretDown, CaretRight, ArrowRight,
} from "@phosphor-icons/react";

const parseDay = (s) => {
  if (!s) return null;
  const iso = String(s).slice(0, 10);
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

function QuickReply({ item, onSent }) {
  const [thread, setThread] = useState(null);
  const rootId = item.key.slice(4);
  useEffect(() => {
    api.get(`/admin/clients/${item.client_user_id}/messages`).then(({ data }) => {
      const msgs = data.filter(m => m.message_id === rootId || m.reply_to_message_id === rootId)
        .sort((a, b) => (a.sent_at || "").localeCompare(b.sent_at || ""));
      setThread(msgs);
    }).catch(() => setThread([]));
  }, [item.client_user_id, rootId]);
  const last = thread?.[thread.length - 1];
  return (
    <div className="mt-1 pt-3 border-t" style={{ borderColor: "var(--hair)" }} data-testid={`queue-quick-reply-${item.key}`}>
      {last && (
        <div className="text-sm mb-2 pl-3 border-l-2" style={{ borderColor: "var(--text)" }}>
          <p className="label">Latest from client · {new Date(last.sent_at).toLocaleString()}</p>
          <p className="whitespace-pre-line mt-1 line-clamp-4" style={{ color: "var(--text-dim)" }}>
            <LinkifiedText text={last.body} />
          </p>
        </div>
      )}
      <AdminThreadReplyBox userId={item.client_user_id} rootId={rootId} rootSubject={item.title}
        client={{ name: item.client_name }} onSent={onSent} />
    </div>
  );
}

function DueChip({ due }) {
  const d = parseDay(due);
  if (!d) return <span className="label px-2 py-0.5" style={{ background: "#e5e7eb", color: "#374151", border: "1px solid #e5e7eb" }}>No due date</span>;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  let bg = "#dbeafe", color = "#1e3a8a", label = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (days < 0) { bg = "#fee2e2"; color = "#991b1b"; label = `${label} · Overdue ${-days}d`; }
  else if (days === 0) { bg = "#fef3c7"; color = "#78350f"; label = `${label} · Today`; }
  else if (days <= 3) { bg = "#fef3c7"; color = "#78350f"; label = `${label} · in ${days}d`; }
  else if (days <= 14) { bg = "#dbeafe"; color = "#1e3a8a"; label = `${label} · in ${days}d`; }
  return <span className="label px-2 py-0.5" style={{ background: bg, color, border: `1px solid ${bg}`, fontWeight: 700 }}>{label}</span>;
}

export default function AdminActionQueue() {
  const location = useLocation();
  const navigate = useNavigate();
  const cached = getCached("admin-action-queue");
  const [clients, setClients] = useState(cached?.clients ?? []);
  const [adminTasks, setAdminTasks] = useState(cached?.adminTasks ?? []);
  const [agencyItems, setAgencyItems] = useState(cached?.agencyItems ?? []);
  const [tab, setTab] = useState(() => {
    const f = new URLSearchParams(location.search).get("filter");
    if (f === "agency") return "agency";
    if (f === "verification") return "verify";
    return "client";
  });
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState({}); // { user_id: { loading, items } }

  // Keep tab in sync with the ?filter=... query param so deep-links from the
  // admin dashboard KPI cards land on the right column
  useEffect(() => {
    const f = new URLSearchParams(location.search).get("filter");
    if (f === "agency" && tab !== "agency") setTab("agency");
    else if (f === "verification" && tab !== "verify") setTab("verify");
    else if (f === "client" && tab !== "client") setTab("client");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const load = async () => {
    try {
      const [c, t, ag] = await Promise.all([
        api.get("/admin/clients"),
        api.get("/admin/tasks"),
        api.get("/admin/agency-due"),
      ]);
      setClients(c.data);
      setAdminTasks(t.data);
      setAgencyItems(ag.data);
      setCached("admin-action-queue", { clients: c.data, adminTasks: t.data, agencyItems: ag.data });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  useEffect(() => { load(); }, []);

  const [replyKey, setReplyKey] = useState(null);
  const markHandled = async (it) => {
    setBusy(it.key);
    try {
      await api.post(`/admin/clients/${it.client_user_id}/messages/${it.key.slice(4)}/handle`);
      setAgencyItems(prev => prev.filter(x => x.key !== it.key));
      toast.success("Thread marked handled");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(null); }
  };

  const nudge = async (uid) => {
    setBusy(`nudge-${uid}`);
    try {
      await api.post(`/admin/clients/${uid}/reminder-task`);
      toast.success("Reminder sent");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(null); }
  };

  const toggleExpand = async (uid) => {
    if (expanded[uid]?.items) {
      setExpanded(prev => { const n = { ...prev }; delete n[uid]; return n; });
      return;
    }
    setExpanded(prev => ({ ...prev, [uid]: { loading: true, items: null } }));
    try {
      const [detailRes, msgsRes, mktRes] = await Promise.all([
        api.get(`/admin/clients/${uid}`),
        api.get(`/admin/clients/${uid}/messages`).catch(() => ({ data: [] })),
        api.get(`/admin/clients/${uid}/marketing-updates`).catch(() => ({ data: [] })),
      ]);
      const detail = detailRes.data || {};
      const items = [];
      const todayIso = new Date().toISOString().slice(0, 10);
      // Onboarding + offboarding pending tasks
      for (const t of detail.tasks || []) {
        if (t.status === "pending") {
          items.push({
            key: `onb-${t.task_id}`,
            title: t.title,
            kind: "Onboarding",
            due_date: t.due_date || null,
            link: `/admin/clients/${uid}`,
          });
        }
      }
      for (const t of detail.offboarding_tasks || []) {
        if (t.status === "pending") {
          items.push({
            key: `off-${t.task_id}`,
            title: t.title,
            kind: "Offboarding",
            due_date: t.due_date || null,
            link: `/admin/clients/${uid}`,
          });
        }
      }
      // Overdue / due payments
      for (const p of detail.payments || []) {
        if (p.status === "paid") continue;
        const due = p.due_date;
        if (due && due <= todayIso) {
          items.push({
            key: `pay-${p.payment_id || due}`,
            title: `Payment: ${p.description || "Invoice"} · $${p.amount_cents ? (p.amount_cents / 100).toFixed(2) : p.amount || "0"}`,
            kind: due < todayIso ? "Overdue payment" : "Payment due today",
            due_date: due,
            link: `/admin/clients/${uid}`,
          });
        }
      }
      // Client-assigned admin tasks (open only)
      for (const t of adminTasks) {
        if (t.status === "complete") continue;
        if (t.assignee_role !== "client") continue;
        const matchUid = t.assignee_user_id || t.client_user_id;
        if (matchUid !== uid) continue;
        items.push({
          key: `atk-${t.task_id}`,
          title: t.title,
          kind: "Admin task",
          due_date: t.due_date || null,
          link: `/admin/tasks`,
        });
      }
      // Samples awaiting client decision
      for (const m of msgsRes.data || []) {
        if (!m.sample) continue;
        const st = m.sample_status || "pending";
        if (st === "pending") {
          items.push({
            key: `smp-${m.message_id}`,
            title: `Sample: ${m.subject || "Awaiting your client's decision"}`,
            kind: "Sample approval",
            due_date: m.due_date || null,
            link: `/admin/clients/${uid}#samples`,
          });
        }
      }
      // Unreplied inbound client messages (agency-side)
      for (const m of msgsRes.data || []) {
        if (m.direction !== "client_to_admin") continue;
        if (m.sample || m.sample_edits) continue;
        if (m.agency_replied) continue;
        items.push({
          key: `msg-${m.message_id}`,
          title: `Reply to: ${m.subject || "(no subject)"}`,
          kind: "Client message",
          due_date: null,
          link: `/admin/clients/${uid}?tab=messages`,
        });
      }
      // Marketing updates awaiting client
      for (const mu of mktRes.data || []) {
        if ((mu.request_status || "") === "pending") {
          items.push({
            key: `mkt-${mu.update_id}`,
            title: `Marketing update: ${mu.title || mu.week_of || "Awaiting approval"}`,
            kind: "Marketing approval",
            due_date: null,
            link: `/admin/clients/${uid}#marketing`,
          });
        }
      }
      // Sort so items with the soonest due date come first, undated at the end
      items.sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"));
      setExpanded(prev => ({ ...prev, [uid]: { loading: false, items } }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
      setExpanded(prev => { const n = { ...prev }; delete n[uid]; return n; });
    }
  };

  // Section 1: Due by client (per-client rollup)
  const clientRows = useMemo(() =>
    clients
      .filter(c => !c.archived && (c.due_by_client || 0) > 0)
      .sort((a, b) => (b.due_by_client || 0) - (a.due_by_client || 0)),
    [clients]);

  // Section 2: Due by agency (open admin tasks assigned to admins)
  // Section 3: Pending verification (needs_verification count per client)
  const verifyRows = useMemo(() =>
    clients
      .filter(c => !c.archived && ((c.progress?.needs_verification) || 0) > 0)
      .sort((a, b) => (b.progress?.needs_verification || 0) - (a.progress?.needs_verification || 0)),
    [clients]);

  const [fClient, setFClient] = useState("");
  const [fKind, setFKind] = useState("");
  const [fDue, setFDue] = useState("");
  const todayIso = new Date().toISOString().slice(0, 10);
  const dueMatch = (d) => !fDue
    || (fDue === "overdue" && d && d < todayIso)
    || (fDue === "today" && d === todayIso)
    || (fDue === "week" && d && d >= todayIso && d <= new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10))
    || (fDue === "none" && !d);
  const shownAgency = agencyItems.filter(it =>
    (!fClient || (fClient === "internal" ? !it.client_user_id : it.client_user_id === fClient))
    && (!fKind || it.kind === fKind) && dueMatch(it.due_date));
  const shownClientRows = clientRows.filter(c => !fClient || c.user_id === fClient);
  const shownVerifyRows = verifyRows.filter(c => !fClient || c.user_id === fClient);
  const kinds = [...new Set(agencyItems.map(it => it.kind))];
  const clientOpts = clients.filter(c => !c.archived)
    .map(c => ({ id: c.user_id, name: c.business_name || c.name }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const filtersOn = fClient || fKind || fDue;

  const totals = {
    client: clientRows.reduce((n, c) => n + (c.due_by_client || 0), 0),
    agency: agencyItems.length,
    verify: verifyRows.reduce((n, c) => n + (c.progress?.needs_verification || 0), 0),
  };

  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="admin-action-queue">
      <header className="flex items-center justify-between px-4 md:px-6 py-6 border-b gap-3" style={{ borderColor: "var(--hair)" }}>
        <div className="flex items-center gap-3 min-w-0">
          <NavButtons home="/admin" testid="queue-back" />
          <div className="min-w-0">
            <p className="label">Console</p>
            <h1 className="title-xl text-2xl md:text-4xl mt-1 truncate">Due list.</h1>
          </div>
        </div>
        <button data-testid="queue-refresh" onClick={load} className="btn-ghost text-xs shrink-0">
          <ArrowsClockwise size={14} weight="bold" /> <span className="hidden sm:inline">Refresh</span>
        </button>
      </header>

      <section className="p-4 md:p-6 space-y-6">
        <FilterPills
          testid="queue-tabs"
          value={tab}
          onChange={(t) => { setTab(t); navigate({ search: `?filter=${t === "verify" ? "verification" : t}` }); }}
          options={[
            { k: "client", label: `Due by client (${totals.client})` },
            { k: "agency", label: `Due by agency (${totals.agency})` },
            { k: "verify", label: `Pending verification (${totals.verify})` },
          ]}
        />

        <div className="flex flex-wrap items-center gap-2" data-testid="queue-filters">
          <select value={fClient} onChange={(e) => setFClient(e.target.value)} data-testid="queue-filter-client"
            className="px-3 py-2 text-xs min-w-[180px]">
            <option value="">All clients</option>
            {tab === "agency" && <option value="internal">Internal (no client)</option>}
            {clientOpts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {tab === "agency" && (
            <>
              <select value={fKind} onChange={(e) => setFKind(e.target.value)} data-testid="queue-filter-kind"
                className="px-3 py-2 text-xs min-w-[180px]">
                <option value="">All item types</option>
                {kinds.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
              <select value={fDue} onChange={(e) => setFDue(e.target.value)} data-testid="queue-filter-due"
                className="px-3 py-2 text-xs min-w-[150px]">
                <option value="">Any due date</option>
                <option value="overdue">Overdue</option>
                <option value="today">Due today</option>
                <option value="week">Due in next 7 days</option>
                <option value="none">No due date</option>
              </select>
            </>
          )}
          {filtersOn && (
            <button onClick={() => { setFClient(""); setFKind(""); setFDue(""); }}
              data-testid="queue-filter-clear" className="btn-ghost text-xs h-8">Clear filters</button>
          )}
          {filtersOn && tab === "agency" && (
            <span className="label" data-testid="queue-filter-count">Showing {shownAgency.length} of {agencyItems.length}</span>
          )}
        </div>

        {tab === "client" && (
          <div className="panel p-2 md:p-4" data-testid="queue-client">
            {shownClientRows.length === 0 ? (
              <p className="label py-8 text-center">Nothing due by any client 🎉</p>
            ) : (
              <div className="space-y-2">
                {shownClientRows.map(c => {
                  const isOpen = !!expanded[c.user_id];
                  const state = expanded[c.user_id];
                  return (
                    <div key={c.user_id} className="hair" data-testid={`queue-client-${c.user_id}`}>
                      <div className="p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <button
                          type="button"
                          data-testid={`queue-expand-${c.user_id}`}
                          onClick={() => toggleExpand(c.user_id)}
                          className="min-w-0 flex-1 text-left flex items-start gap-2">
                          {isOpen ? <CaretDown size={16} weight="bold" className="mt-0.5 shrink-0" />
                                  : <CaretRight size={16} weight="bold" className="mt-0.5 shrink-0" />}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className="font-medium">{c.business_name || c.name}</span>
                              <span className="label" style={{ overflowWrap: "anywhere" }}>{c.email}</span>
                            </div>
                            <p className="label mt-1">
                              {c.progress?.pending || 0} onboarding pending
                              {c.due_by_client - (c.progress?.pending || 0) > 0 && ` · ${c.due_by_client - (c.progress?.pending || 0)} other item(s)`}
                            </p>
                          </div>
                        </button>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="label px-2 py-0.5" style={{ background: "#fef3c7", color: "#78350f", border: "1px solid #fef3c7" }}>
                            {c.due_by_client}
                          </span>
                          <button data-testid={`queue-nudge-${c.user_id}`}
                            disabled={busy === `nudge-${c.user_id}`}
                            onClick={() => nudge(c.user_id)}
                            className="btn-primary text-xs whitespace-nowrap disabled:opacity-40">
                            <PaperPlaneTilt size={12} weight="bold" /> Send reminder
                          </button>
                        </div>
                      </div>
                      {isOpen && (
                        <div className="px-3 md:px-6 pb-3 border-t" style={{ borderColor: "var(--hair)" }}
                             data-testid={`queue-expand-body-${c.user_id}`}>
                          {state?.loading && <p className="label py-3">Loading…</p>}
                          {state?.items && state.items.length === 0 && (
                            <p className="label py-3">No pending items to list</p>
                          )}
                          {state?.items && state.items.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {state.items.map(it => (
                                <div key={it.key}
                                  data-testid={`queue-item-${it.key}`}
                                  className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 py-1.5">
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium truncate">{it.title}</p>
                                    <p className="label mt-0.5">{it.kind}</p>
                                  </div>
                                  <DueChip due={it.due_date} />
                                </div>
                              ))}
                              <Link to={`/admin/clients/${c.user_id}`}
                                data-testid={`queue-open-profile-${c.user_id}`}
                                className="btn-ghost text-xs mt-2 whitespace-nowrap inline-flex">
                                Open client profile <ArrowRight size={12} weight="bold" />
                              </Link>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === "agency" && (
          <div className="panel p-2 md:p-4" data-testid="queue-agency">
            {shownAgency.length === 0 ? (
              <p className="label py-8 text-center">{agencyItems.length ? "No items match these filters" : "Inbox zero — nothing due by the agency"}</p>
            ) : (
              <div className="space-y-2">
                {shownAgency.map(it => (
                  <div key={it.key}
                    className="hair p-3 flex flex-col md:flex-row md:flex-wrap md:items-center md:justify-between gap-3"
                    data-testid={`queue-agency-${it.key}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <p className="font-medium">{it.title}</p>
                        <span className="label">{it.kind}</span>
                        {it.client_user_id ? (
                          <Link to={`/admin/clients/${it.client_user_id}`} className="label underline underline-offset-2">
                            {it.client_name || "client"}
                          </Link>
                        ) : <span className="label">Internal{it.assignee ? ` · ${it.assignee}` : ""}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 flex-wrap">
                      <DueChip due={it.due_date} />
                      {it.key.startsWith("msg-") && (
                        <button onClick={() => setReplyKey(replyKey === it.key ? null : it.key)}
                          data-testid={`queue-agency-reply-${it.key}`} className="btn-ghost text-xs whitespace-nowrap">
                          <PaperPlaneTilt size={12} weight="bold" /> {replyKey === it.key ? "Hide reply" : "Reply"}
                        </button>
                      )}
                      {it.key.startsWith("msg-") && (
                        <button onClick={() => markHandled(it)} disabled={busy === it.key}
                          data-testid={`queue-agency-handle-${it.key}`}
                          className="btn-ghost text-xs whitespace-nowrap disabled:opacity-40"
                          title="Mark this thread handled (no email to the client)">
                          <CheckCircle size={12} weight="bold" /> {busy === it.key ? "Saving…" : "Mark handled"}
                        </button>
                      )}
                      <Link to={it.link} className="btn-ghost text-xs whitespace-nowrap"
                        data-testid={`queue-agency-open-${it.key}`}>
                        Open →
                      </Link>
                    </div>
                    {replyKey === it.key && (
                      <div className="w-full md:basis-full">
                        <QuickReply item={it} onSent={() => {
                          setReplyKey(null);
                          setAgencyItems(prev => prev.filter(x => x.key !== it.key));
                        }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "verify" && (
          <div className="panel p-2 md:p-4" data-testid="queue-verify">
            {shownVerifyRows.length === 0 ? (
              <p className="label py-8 text-center">Nothing awaiting verification</p>
            ) : (
              <div className="space-y-2">
                {shownVerifyRows.map(c => (
                  <div key={c.user_id}
                    className="hair p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                    data-testid={`queue-verify-${c.user_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <Link to={`/admin/clients/${c.user_id}`} className="font-medium hover:underline">
                          {c.business_name || c.name}
                        </Link>
                        <span className="label" style={{ overflowWrap: "anywhere" }}>{c.email}</span>
                      </div>
                      <p className="label mt-1 flex items-center gap-1">
                        <WarningCircle size={12} weight="bold" />
                        {c.progress?.needs_verification || 0} task(s) awaiting your verification
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="label px-2 py-0.5" style={{ background: "#fef3c7", color: "#78350f", border: "1px solid #fef3c7" }}>
                        {c.progress?.needs_verification}
                      </span>
                      <Link to={`/admin/clients/${c.user_id}`} className="btn-primary text-xs whitespace-nowrap"
                        data-testid={`queue-verify-open-${c.user_id}`}>
                        <CheckCircle size={12} weight="bold" /> Review
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
